import {
  Box,
  Chip,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import { memo } from "react";
import { SideBarScroll } from "src/views/assistant/components/ui/SideBarScroll";
import type {
  ItemEquivalencyRecord,
  PartSnapshot,
} from "../services/ItemEquivalencyService";

/**
 * The snapshots, not live catalog rows: part records get edited and deleted,
 * and the evaluation's basis has to stay what the record captured at kickoff.
 */
const PartCard = ({
  role,
  part,
}: {
  role: "Installed" | "Candidate";
  part: PartSnapshot;
}) => {
  const featureKeys =
    part.feature_order.length > 0
      ? part.feature_order
      : Object.keys(part.features);
  return (
    <Box data-testid={`peq-parts-tab-${role.toLowerCase()}`}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
        <Chip size="small" variant="outlined" label={role} />
        <Typography variant="subtitle2" noWrap>
          {part.name}
        </Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary" component="div">
        {[part.manufacturer, part.part_type, part.material_number]
          .filter(Boolean)
          .join(" · ") || "No catalog details"}
      </Typography>
      {featureKeys.length > 0 && (
        <Table size="small" sx={{ mt: 1 }}>
          <TableBody>
            {featureKeys.map((key) => (
              <TableRow key={key}>
                <TableCell sx={{ color: "text.secondary", width: "45%" }}>
                  {key}
                </TableCell>
                <TableCell>{part.features[key] ?? "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
};

/** The two parts this evaluation compares, as captured on the record. */
const ItemEquivalencyPartsTab = memo(
  ({ record }: { record: ItemEquivalencyRecord }) => {
    const { original_part_snapshot: original, replacement_part_snapshot: replacement } =
      record;
    if (!original && !replacement) {
      return (
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            This evaluation was started without catalog parts.
          </Typography>
        </Box>
      );
    }
    return (
      <SideBarScroll>
        <Stack spacing={2} sx={{ px: 1.5, py: 1 }}>
          {original && <PartCard role="Installed" part={original} />}
          {original && replacement && <Divider />}
          {replacement && <PartCard role="Candidate" part={replacement} />}
        </Stack>
      </SideBarScroll>
    );
  },
);

ItemEquivalencyPartsTab.displayName = "ItemEquivalencyPartsTab";

export default ItemEquivalencyPartsTab;
